import type { AdminOrderDetailDto, AdminRefundPolicy } from '@carcheck/shared';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { observer } from 'mobx-react-lite';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ForceCancelDialog } from './_components/ForceCancelDialog';
import { OrderDetailFooter } from './_components/OrderDetailFooter';
import { OrderDetailHeader } from './_components/OrderDetailHeader';
import { OverviewTab } from './_components/OverviewTab';
import { PaymentTab } from './_components/PaymentTab';
import { TimelineTab } from './_components/TimelineTab';
import { isForceCancellable } from './_lib/forceCancellable';
import { useForceCancel } from './_lib/useForceCancel';
import { useAdminOrderDetail } from '../../api/admin-orders';
import { EntityAuditTrail } from '../../components/audit';

/**
 * Order detail drawer (AUT-123 / ADMIN-11).
 *
 * Right-side 720px drawer with tabs Обзор / Таймлайн / Платёж / Журнал +
 * a footer force-cancel (`useForceCancel`; role + permission + audit gated
 * server-side). On success the `['admin','orders']` cache prefix is invalidated so the
 * list + this drawer refetch (NOT optimistic-patched — the force-cancel
 * envelope's `dto` is the user-facing OrderDto, a different shape).
 *
 * The force-cancel footer button is hidden in terminal states
 * (completed / released / cancelled / expired) via `isForceCancellable`
 * — pure UX; the backend stays the 409 authority.
 *
 * Opens when `orderId` is non-null; the detail query is `enabled` only
 * then, so closing it stops the fetch.
 */
const DRAWER_WIDTH = 720;

type TabKey = 'overview' | 'timeline' | 'payment' | 'log';

interface OrderDetailDrawerProps {
  orderId: string | null;
  onClose: () => void;
}

const DrawerBody = observer(
  ({ order, onClose }: { order: AdminOrderDetailDto; onClose: () => void }): ReactElement => {
    const { t } = useTranslation();
    const [tab, setTab] = useState<TabKey>('overview');
    const [cancelOpen, setCancelOpen] = useState(false);
    const { busy, run } = useForceCancel();

    const cancellable = isForceCancellable(order.status);

    const onConfirm = (input: { reason: string; refundPolicy: AdminRefundPolicy }): void => {
      void run({ id: order.id, reason: input.reason, refundPolicy: input.refundPolicy }).then(
        (ok) => {
          if (ok) setCancelOpen(false);
        },
      );
    };

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            maxWidth: '100vw',
          }}
        >
          <OrderDetailHeader order={order} onClose={onClose} />

          <Tabs
            value={tab}
            onChange={(_e, v: TabKey) => setTab(v)}
            sx={{ px: 3, borderBottom: '1px solid', borderColor: 'divider', minHeight: 44 }}
          >
            <Tab value="overview" label={t('orders.detail.tabs.overview')} />
            <Tab value="timeline" label={t('orders.detail.tabs.timeline')} />
            <Tab value="payment" label={t('orders.detail.tabs.payment')} />
            <Tab value="log" label={t('orders.detail.tabs.log')} />
          </Tabs>

          <Box sx={{ flex: 1, overflow: 'auto', p: '22px 24px' }}>
            {tab === 'overview' ? <OverviewTab order={order} /> : null}
            {tab === 'timeline' ? <TimelineTab order={order} /> : null}
            {tab === 'payment' ? <PaymentTab order={order} /> : null}
            {tab === 'log' ? <EntityAuditTrail entityType="Order" entityId={order.id} /> : null}
          </Box>

          <OrderDetailFooter
            cancellable={cancellable}
            busy={busy}
            onForceCancel={() => setCancelOpen(true)}
          />
        </Box>

        <ForceCancelDialog
          open={cancelOpen}
          loading={busy}
          onConfirm={onConfirm}
          onCancel={() => setCancelOpen(false)}
        />
      </>
    );
  },
);

export const OrderDetailDrawer = ({ orderId, onClose }: OrderDetailDrawerProps): ReactElement => {
  const { t } = useTranslation();
  const query = useAdminOrderDetail(orderId);

  return (
    <Drawer anchor="right" open={orderId !== null} onClose={onClose}>
      {query.isLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: '100vw', sm: DRAWER_WIDTH },
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      ) : null}
      {query.error ? (
        <Box sx={{ width: { xs: '100vw', sm: DRAWER_WIDTH }, p: 3 }}>
          <Alert severity="error">{t('orders.detail.loadError')}</Alert>
        </Box>
      ) : null}
      {query.data ? <DrawerBody key={query.data.id} order={query.data} onClose={onClose} /> : null}
    </Drawer>
  );
};
